import type { BookingConfirmation } from "./types";

function escapeIcs(value: string) {
  return value
    .replaceAll("\\", "\\\\")
    .replaceAll(";", "\\;")
    .replaceAll(",", "\\,")
    .replaceAll(/\r\n|\r|\n/g, "\\n");
}

function utcStamp(value: string) {
  return new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function foldLine(line: string) {
  const encoder = new TextEncoder();
  const chunks: string[] = [];
  let chunk = "";

  for (const character of line) {
    const continuationPrefixBytes = chunks.length ? 1 : 0;
    if (chunk && encoder.encode(chunk + character).length + continuationPrefixBytes > 75) {
      chunks.push(chunk);
      chunk = character;
    } else {
      chunk += character;
    }
  }

  chunks.push(chunk);
  return chunks.map((value, index) => (index ? ` ${value}` : value)).join("\r\n");
}

export function bookingToIcs(booking: BookingConfirmation) {
  const description = booking.meeting_url
    ? `Join the meeting: ${booking.meeting_url}`
    : `${booking.location_label}. RSM will share any final joining details separately.`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//RSM Consulting//Booking Desk//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${escapeIcs(booking.booking_id)}@rsm-consulting`,
    `DTSTAMP:${utcStamp(new Date().toISOString())}`,
    `DTSTART:${utcStamp(booking.starts_at)}`,
    `DTEND:${utcStamp(booking.ends_at)}`,
    `SUMMARY:${escapeIcs(booking.title)} with ${escapeIcs(booking.host_name)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    `LOCATION:${escapeIcs(booking.meeting_url ?? booking.location_label)}`,
    `ATTENDEE;CN=${escapeIcs(booking.guest_name)}:mailto:${escapeIcs(booking.guest_email)}`,
    `STATUS:CONFIRMED`,
    `SEQUENCE:0`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `${lines.map(foldLine).join("\r\n")}\r\n`;
}

export function downloadBookingIcs(booking: BookingConfirmation) {
  const blob = new Blob([bookingToIcs(booking)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `rsm-${booking.reference.toLowerCase()}.ics`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function googleCalendarUrl(booking: BookingConfirmation) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${booking.title} with ${booking.host_name}`,
    dates: `${utcStamp(booking.starts_at)}/${utcStamp(booking.ends_at)}`,
    details: booking.meeting_url
      ? `Join: ${booking.meeting_url}`
      : "Booked through RSM Booking Desk.",
    location: booking.meeting_url ?? booking.location_label,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
